import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { partnersProspects } from 'rails_routes'
import { useDeleteQuotingProspectsProspectIdUploadsOtherFilesBlobId, usePostQuotingProspectsProspectIdUploadsOtherFiles } from 'utils/web_api'
import { type Document, QuotingProspectQuoteRequestStatus } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { QuoteRequestStep } from '..'
import { useProspect } from '../ProspectProvider'
import { QuoteRequestUploadPage } from '../QuoteRequestUploadPage'
import { useQuoteRequestProgress } from '../useQuoteRequestProgress'

interface OtherFilesUploadPageProps {
  accept?: string[]
  returnToProspect?: boolean
}

export function OtherFilesUploadPage ({ accept, returnToProspect }: OtherFilesUploadPageProps) {
  const { assign } = useWindowLocation()
  const { prospect, refetch } = useProspect()
  const navigate = useNavigate()
  const { completeStep } = useQuoteRequestProgress({
    step: QuotingProspectQuoteRequestStatus.other_files,
    onSuccess: () => navigate(`../../${QuoteRequestStep.SUMMARY}`)
  })
  const [isUploading, setUploading] = useState(false)
  const [error, setError] = useState<{ title: string, errors: string[] }>()
  const formDataRef = useRef<FormData>()

  const { mutate: uploadOtherFiles } = usePostQuotingProspectsProspectIdUploadsOtherFiles({
    mutation: {
      onSuccess: () => {
        setUploading(false)
        window.segment?.track('Other files upload succeeded', { id: prospect?.id })
        refetch()
      },
      onError: (err: GrapeAPIErrorType) => {
        setUploading(false)
        window.segment?.track('Other files upload failed', { id: prospect?.id, errors: err.errors })
        setError({
          title: err.message,
          errors: err.errors
        })
      }
    },
    request: {
      get body () { return formDataRef.current }
    }
  })

  const { mutate: deleteOtherFile } = useDeleteQuotingProspectsProspectIdUploadsOtherFilesBlobId({
    mutation: {
      onSuccess: () => {
        window.segment?.track('Other file deleted', { id: prospect?.id })
        refetch()
      },
      onError: (err: GrapeAPIErrorType) => {
        setError({
          title: err.message,
          errors: err.errors
        })
      }
    },
  })

  if (!prospect) {
    return null
  }

  const onUpload = (files: File[]) => {
    setError(undefined)
    const formData = new FormData()
    files.forEach(file => formData.append('files[]', file))
    formDataRef.current = formData
    setUploading(true)
    window.segment?.track('Other files upload attempted', { id: prospect?.id, amount: files.length })
    uploadOtherFiles({
      prospectId: prospect.id
    })
  }

  const onDelete = (file: Document) => {
    deleteOtherFile({ prospectId: prospect.id, blobId: file.id })
  }

  const onBack = () => {
    if (returnToProspect) {
      assign(partnersProspects.show({ id: prospect.id }))
    } else {
      navigate(`../../${prospect.offers_health_insurance_today
        ? QuoteRequestStep.SBCS_UPLOAD
        : QuoteRequestStep.CENSUS_UPLOAD}`)
    }
  }
  const onNext = () => {
    returnToProspect
      ? assign(partnersProspects.show({ id: prospect.id }))
      : completeStep()
  }

  return (
    <QuoteRequestUploadPage
      title='Upload other files'
      files={prospect.other_file_uploads || []}
      accept={accept?.join(',')}
      multiple
      error={error}
      isUploading={isUploading}
      onUpload={onUpload}
      onDelete={onDelete}
      onBack={onBack}
      onNext={onNext}
      nextButtonText={returnToProspect ? 'Return' : undefined}
      canProceed
    >
      <div className='text-gray-500 text-sm'>
        <p className='mb-2'>Upload any additional files that would help our team produce a more accurate quote. This can include:</p>
        <ul className='list-disc list-outside pl-5'>
          <li>Rate sheets</li>
          <li>Claims data</li>
          <li>Anything else you can share about {prospect.name}'s benefits goals.</li>
        </ul>
      </div>
    </QuoteRequestUploadPage>
  )
}
